import type {
  Album,
  ChatConversationSummary,
  ChatMessage,
  ChatReplyTo,
  CreditsStreak,
  GameIncomingInvite,
  GameSession,
  Gift,
  GiftCatalogItem,
  MediaItem,
  PartyIncomingInvite,
  PartyReactionEvent,
  PartySession,
  SortState,
  TogetherIncomingInvite,
  TogetherReactionEvent,
  TogetherSchedule,
  TogetherScheduleReminder,
  TogetherSession,
  PresenceUser,
  ViewMode,
  WheelIncomingInvite,
  WheelReactionEvent,
  WheelSession,
} from "./types";
import { getSharedState } from "./windowBus";

export interface AppState {
  currentAlbum: string;
  currentUserEmail: string;
  // Seeded once from /api/me in AuthGate.tsx, updated by ProfileScreen.tsx
  // on a successful name/avatar change - the single shared source every
  // avatar/name-displaying surface (CoupleHeader.tsx, ProfileScreen.tsx)
  // reads from, replacing what used to be each of them independently
  // re-fetching /api/me on their own mount.
  currentUserFullName: string;
  currentUserAvatarFilename: string | null;
  albums: Album[];
  media: MediaItem[];
  viewMode: ViewMode;
  // Now only drives the periodic media-auth token refresh - everything else
  // that used to share this interval (gallery/albums/notifications/gifts/
  // presence/schedules) moved to WS pushes + onSocketOpen resync, see
  // AuthGate.tsx.
  mediaTokenTimer: ReturnType<typeof setInterval> | null;
  currentMediaIndex: number;
  favoritesSet: Set<string>;
  sortState: SortState;
  searchQuery: string;

  selectMode: boolean;
  selectedFilenames: Set<string>;
  favoritesOnly: boolean;

  // Albums/Chat each have their own sub-state: null means the list view,
  // an id means you've drilled into that album/conversation's detail.
  // (Phase 10: activeTab was removed - react-router's URL is now the
  // single source of truth for "which top-level screen," so tracking it
  // separately in appState too was redundant. MemoriesRow.tsx's
  // Home-only condition uses useLocation() instead.)
  albumsDetailId: string | null;

  chatThreadId: string | null;
  chatConversations: ChatConversationSummary[];
  // Only the currently-open thread's messages - owned by ChatThreadScreen.tsx,
  // but appState (not component-local state) so ChatScreen.tsx's socket
  // handlers (setChatSocketHandlers) can push server-pushed updates into it
  // regardless of which component subtree happens to own the render.
  chatThreadMessages: ChatMessage[];
  // Whether the currently-loaded chatThreadMessages window has more history
  // beyond either edge - appState (not component-local state) so
  // ChatScreen.tsx's onNewMessage handler can check chatThreadHasMoreNewer
  // before deciding whether to append a live WS push to the tail: if the
  // user has jumped to a search result/date in the middle of history (so
  // chatThreadHasMoreNewer is true - the loaded window isn't actually the
  // live tail), silently appending a new message there would look wrong,
  // stitched onto content that isn't really adjacent to "now".
  chatThreadHasMoreOlder: boolean;
  chatThreadHasMoreNewer: boolean;
  // Set (with the sender's own auto-clear safety net) while the OTHER
  // participant of this conversation is actively typing - null otherwise.
  // A single field, not per-conversation, is enough for this 2-person app
  // (only ever one open thread, one possible "other" typist at a time).
  typingConversationId: string | null;

  // Set by ChatThreadRouteScreen.tsx when /chat/:conversationId is reached
  // via a link that wants the compose box to open already replying to a
  // specific message (currently: GiftViewerDialog's Reply button, V2 Part
  // E) - ChatThreadScreen.tsx consumes it once on mount, then clears it.
  chatPendingReplyTo: ChatReplyTo | null;

  gifts: Gift[];

  // Private Drops V2: any component can request "open the reveal dialog for
  // this gift" (a chat card tap, the Open Together waiting room's own
  // synchronized start, the list card) by setting this id - mirrors
  // togetherAwaitingRejoin's own "shared flag any component can react to"
  // shape. GiftsScreen.tsx's GiftsBody (always mounted) watches it, resolves
  // the gift from the array above, opens GiftViewerDialog, then clears it.
  openingGiftId: string | null;
  // True once the first loadGiftsData() call has settled (success or
  // failure) - lets PrivateDropChatCard.tsx tell "still loading" apart from
  // "genuinely gone" (a cancelled drop is filtered out of the array
  // server-side, not just absent while loading) without a separate fetch of
  // its own.
  giftsLoaded: boolean;

  // Gift Experience V2 - server-authoritative, this is just a read cache
  // for display (SendGiftScreen.tsx/RewardsScreen.tsx/GiftsScreen.tsx).
  // Every actual balance change comes from a server response
  // (creditsApi.ts), never computed/guessed client-side.
  credits: number;
  creditsStreak: CreditsStreak;
  giftCatalog: GiftCatalogItem[];

  // Every other directory user's online/offline state (PresenceIndicator.tsx).
  presenceUsers: PresenceUser[];

  reelDialogOpen: boolean;

  // Together Mode: an invite someone else sent that this user hasn't
  // responded to yet (null = no pending incoming invite showing).
  togetherIncomingInvite: TogetherIncomingInvite | null;
  // The session THIS user is currently in (as inviter or recipient),
  // pending or active - null when not in one.
  togetherSession: TogetherSession | null;
  // True from the moment a session is DISCOVERED (fresh boot/refresh, or a
  // reconnect that finds a session this client didn't already locally know
  // about) until the user explicitly rejoins (taps the Rejoin pill) or the
  // session ends. Gates TogetherRoot.tsx's auto-open-Lightbox effect so
  // passive discovery never force-navigates - only an explicit Rejoin tap
  // does. See togetherSync.ts's discoverOrResyncTogetherSession.
  togetherAwaitingRejoin: boolean;
  // Floating reaction bursts to render - self-pruning (ReactionBurst
  // removes its own entry once its animation finishes), so this is a
  // rolling append-only-in-practice list, not a full history.
  togetherReactions: TogetherReactionEvent[];
  // The PARTNER's currently-building press-and-hold reaction (own hold is
  // rendered from local component state, zero round-trip - see
  // TogetherRoot.tsx). A single mutable slot, not a list: updated in place
  // as intensity climbs (together_reaction_hold), cleared on
  // together_reaction_hold_end or a client-side self-healing timeout if no
  // repeat update arrives for ~1.8s (covers a sender whose tab dies
  // mid-gesture with another tab still open, so no hold_end ever arrives).
  togetherHoldReaction: { emoji: string; fromUserId: string; intensity: number } | null;
  togetherSchedules: TogetherSchedule[];
  // "It's time - Start Now?" toast trigger for a schedule whose startsAt
  // has arrived; null when there's nothing to prompt.
  togetherScheduleReminder: TogetherScheduleReminder | null;
  // Mirrors gameDialogOpen's pattern exactly - set by whichever trigger the
  // user tapped (Lightbox.tsx's action bar), read by TogetherRoot.tsx,
  // so the invite dialog isn't only reachable from TogetherRoot's own
  // now-removed floating button.
  togetherInviteDialogOpen: boolean;

  // Game feature: mirrors the Together Mode fields above exactly (same
  // "one session per user, ephemeral, server-authoritative" shape).
  gameIncomingInvite: GameIncomingInvite | null;
  gameSession: GameSession | null;
  // Set by whichever trigger the user tapped (Home's Play Together
  // activity card, or ChatThreadScreen.tsx's "Start an activity" menu),
  // read by GameRoot.tsx - opens the mode-picker/invite dialog. Not needed once a
  // session already exists (GameRoot shows the active stage instead).
  gameDialogOpen: boolean;

  // Party Room (self-hosted shared listening): mirrors the Together Mode
  // fields above exactly, but for a shared music queue instead of a
  // gallery item. Not tied to the Lightbox - can run in the background
  // while browsing any screen, since it drives the same singleton
  // #audioPlayer element MusicPanel.tsx always keeps mounted.
  partyIncomingInvite: PartyIncomingInvite | null;
  partySession: PartySession | null;
  partyReactions: PartyReactionEvent[];
  // Set by ChatThreadScreen.tsx's "Start an activity" menu, read by
  // PartyRoot.tsx - same appState-driven cross-component trigger pattern
  // as togetherInviteDialogOpen/gameDialogOpen.
  partyInviteDialogOpen: boolean;

  // Couple Wheel: mirrors the Game fields above exactly (same "one session
  // per user, ephemeral, server-authoritative" shape) - no dialogOpen flag
  // though, since /couple-wheel is a real route (CoupleWheelPage.tsx),
  // not a trigger-driven overlay.
  wheelIncomingInvite: WheelIncomingInvite | null;
  wheelSession: WheelSession | null;
  wheelReactions: WheelReactionEvent[];

  uploadInProgress: boolean;
  filePickerOpen: boolean;
  pickerTimeoutId: ReturnType<typeof setTimeout> | null;

  pendingDeleteName: string | null;

  autolockArmed: boolean;
  inactivityTimer: ReturnType<typeof setTimeout> | null;
  mediaPlaying: boolean;
  // Mirrors Lightbox.tsx's own useAutoHideControls() hidden state, so
  // TogetherRoot.tsx's overlays (portaled separately to document.body, no
  // direct relationship to Lightbox's component tree) can fade out in sync
  // with the native video controls instead of staying permanently visible.
  mediaControlsHidden: boolean;
  // Video playback audit, Bug A: set when a peer-triggered together_sync
  // play() is rejected specifically by Safari/WebKit's user-activation gate
  // (NotAllowedError), not merely "not buffered yet" (the existing canplay
  // retry already covers that). A permission rejection can't be recovered
  // by retrying the same no-gesture call, so TogetherRoot.tsx renders a
  // real tappable affordance instead - tapping it calls video.play() from
  // inside the click handler, which carries the gesture the earlier
  // WS-triggered call didn't.
  togetherPlaybackBlocked: boolean;

  // Premium Album Detail redesign: true while GalleryRouteScreen.tsx's own
  // loadGalleryData() call (on mount / albumId change / manual refresh) is
  // in flight - lets AlbumHeroHeader.tsx show a real hero/toolbar skeleton
  // and GalleryGrid.tsx suppress its "No media yet" hint until data has
  // actually had a chance to arrive, instead of both flashing an empty
  // state on every album switch.
  galleryLoading: boolean;
}

function createInitialState(): AppState {
  return {
    currentAlbum: "all",
    currentUserEmail: "",
    currentUserFullName: "",
    currentUserAvatarFilename: null,
    albums: [],
    media: [],
    viewMode: "grid",
    mediaTokenTimer: null,
    currentMediaIndex: -1,
    favoritesSet: new Set<string>(),
    sortState: { key: "date", dir: "desc" },
    searchQuery: "",

    selectMode: false,
    selectedFilenames: new Set<string>(),
    favoritesOnly: false,

    albumsDetailId: null,

    chatThreadId: null,
    chatConversations: [],
    chatThreadMessages: [],
    chatThreadHasMoreOlder: false,
    chatThreadHasMoreNewer: false,
    typingConversationId: null,
    chatPendingReplyTo: null,

    gifts: [],
    openingGiftId: null,
    giftsLoaded: false,
    credits: 0,
    creditsStreak: { lastRewardDate: null, currentStreak: 0, longestStreak: 0 },
    giftCatalog: [],

    presenceUsers: [],

    reelDialogOpen: false,

    togetherIncomingInvite: null,
    togetherSession: null,
    togetherAwaitingRejoin: false,
    togetherReactions: [],
    togetherHoldReaction: null,
    togetherSchedules: [],
    togetherScheduleReminder: null,
    togetherInviteDialogOpen: false,

    gameIncomingInvite: null,
    gameSession: null,
    gameDialogOpen: false,

    partyIncomingInvite: null,
    partySession: null,
    partyReactions: [],
    partyInviteDialogOpen: false,

    wheelIncomingInvite: null,
    wheelSession: null,
    wheelReactions: [],

    // Guard flags so upload/file-picker activity doesn't trigger auto-lock
    uploadInProgress: false,
    filePickerOpen: false,
    pickerTimeoutId: null,

    pendingDeleteName: null,

    autolockArmed: false,
    inactivityTimer: null,
    // True while the fullscreen video or the music player is actively
    // playing - passive video/music playback shouldn't count as idle time.
    mediaPlaying: false,
    mediaControlsHidden: false,
    togetherPlaybackBlocked: false,
    galleryLoading: false,
  };
}

// Single mutable state container. Modules import `appState` and mutate its
// properties directly (ESM re-exports of bare `let` bindings can't be
// reassigned by importers, so a shared object is simpler than juggling
// getters/setters across a dozen files).
//
// Backed by windowBus, not a plain `export const` object literal - app.js
// and react-app.js are separately-bundled IIFEs, so a plain module-level
// object would be duplicated (each bundle gets its own instance) rather
// than actually shared. getSharedState ensures both bundles reference the
// literal same object, whichever one initializes it first. Pair any
// mutation a React-migrated feature also reads with a stateBus
// notifyStateChange() call - see stateBus.ts.
export const appState = getSharedState<AppState>("__appState", createInitialState);
